<template>
  <div class="page home" :data-foo="homePage.spotlight_enabled">
    <drawer :page="drawerPage" :back-page="drawerBackPage"/>
    <spotlight :enabled="homePage.spotlight_enabled === '1'" :max-fade="parseInt(homePage.spotlight_max_fade, 10)">
      <spotlight-image v-if="spotlightBackgroundImage" :file="spotlightBackgroundImage"/>
      <spotlight-video v-else-if="spotlightBackgroundVideo" :file="spotlightBackgroundVideo"/>
      <spotlight-word-map v-else-if="homePage.word_map && homePage.word_map.length" :word-map="homePage.word_map" :background-color="homePage.word_map_background_color"/>
    </spotlight>

    <section class="home-section text-xs-left pa-6">
      <a :href="`#${sections[0].uid}`" v-scroll-to="{ el: `#${sections[0].uid}`, offset: -50 }" class="home-name">{{ homePage.name }}</a>
      <div>{{ homePage.subname }}</div>
    </section>

    <v-container class="main-container pa-0 ml-0 pl-6 pr-6">
      <v-layout row class="mt-100vh">
        <v-flex v-if="!$vuetify.breakpoint.smAndDown" class="navigation-column" md12 order-md2 :class="{ 'mb-7': $vuetify.breakpoint.smAndDown }" @click="onClickNavigationContainer">
          <navigation :section-pages="sections" :drawer-open="!!drawerPage.uid"></navigation>
        </v-flex>
        <v-flex class="content-column" order-md1 ref="content" :class="{ 'pr-7': !$vuetify.breakpoint.smAndDown }">
          <content-section
            v-for="(section, index) in sections"
            :class="{ 'pt-7': index !== 0, [`section-${index}`]: true }"
            :uid="section.uid"
            :key="section.uid"/>
          <div class="mb-6">
            <print-button />
            <facebook-share-button />
            <twitter-share-button />
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <v-bottom-sheet v-model="sheet" class="elevation-0 pa-3" v-if="$vuetify.breakpoint.smAndDown">
      <v-layout @click="sheet = !sheet">
        <v-flex>
          <button @click.stop="sheet = true" v-if="!sheet">
            <span class="icon icon-hamburger"></span>
          </button>
          <navigation :section-pages="sections"></navigation>
        </v-flex>
        <v-flex class="text-xs-right">
          <v-fade-transition>
            <button @click.stop="sheet = false">
              <span v-if="sheet" class="icon icon-close"></span>
            </button>
          </v-fade-transition>
        </v-flex>
      </v-layout>
    </v-bottom-sheet>

  </div>
</template>

<script>
import Navigation from '@/components/Navigation'
import Arrow from '@/components/Arrow'
import ContentSection from '@/components/ContentSection'
import TitleAnimation from '@/components/TitleAnimation'
import FacebookShareButton from '@/components/FacebookShareButton'
import PrintButton from '@/components/PrintButton'
import TwitterShareButton from '@/components/TwitterShareButton'
import Drawer from '@/components/Drawer'
import Spotlight from '@/components/Spotlight'
import SpotlightVideo from '@/components/SpotlightVideo'
import SpotlightImage from '@/components/SpotlightImage'
import SpotlightWordMap from '@/components/SpotlightWordMap'

export default {
  name: 'home',
  components: {
    Navigation,
    Arrow,
    TitleAnimation,
    ContentSection,
    FacebookShareButton,
    PrintButton,
    TwitterShareButton,
    Drawer,
    Spotlight,
    SpotlightVideo,
    SpotlightImage,
    SpotlightWordMap
  },
  metaInfo () {
    let title = this.$store.state.site.site.title
    let description = this.$store.state.site.site.description
    let url = this.$store.state.site.site.url
    let author = this.$store.state.site.site.author
    if (this.drawerPage.uid) {
      title += ` | ${this.drawerPage.title}`
      url = this.drawerPage.url
      author = this.drawerPage.author

      // strip html from description string
      const html = this.drawerPage.text
      const div = document.createElement('div')
      div.innerHTML = html
      description = (div.textContent || div.innerText || '').split('\n').find(line => !!line)
    }

    return {
      title,
      meta: [{
        vmid: 'description',
        name: 'description',
        content: description
      },
      {
        vmid: 'twitter:description',
        name: 'twitter:description',
        content: description
      },
      {
        vmid: 'twitter:title',
        name: 'twitter:title',
        content: title
      },
      {
        'property': 'og:description',
        'content': description,
        'vmid': 'og:description'
      },
      {
        'property': 'og:title',
        'content': title,
        'vmid': 'og:title'
      },
      {
        'property': 'article:author',
        'content': author,
        'vmid': 'article:author'
      },
      {
        'property': 'og:url',
        'content': url,
        'vmid': 'og:url'
      }]
    }
  },
  data () {
    return {
      sheet: false
    }
  },
  computed: {
    routeParams () {
      const routeParams = this.$route.path.split('/')
      routeParams.shift()
      return routeParams
    },
    sections () {
      return this.$store.state.pages.pages.filter(page => ['home', 'error'].indexOf(page.type) < 0)
    },
    pageId () {
      return this.routeParams[this.routeParams.length - 1]
    },
    backPageId () {
      return this.routeParams[this.routeParams.length - 2]
    },
    superPageId () {
      return this.routeParams[0]
    },
    drawerBackPage () {
      if (!this.backPageId) {
        return this.$store.getters.getPageByUID('home')
      }

      return this.$store.getters.getPageByUID(this.backPageId)
    },
    drawerPage () {
      if (!this.pageId) {
        return {}
      }

      return this.$store.getters.getPageByUID(this.pageId)
    },
    offset () {
      if (this.$vuetify.breakpoint.xsOnly) {
        return 15
      }

      if (this.$vuetify.breakpoint.smAndDown) {
        return 30
      }

      return 60
    },
    homePage () {
      return this.$store.getters.getPageByUID('home')
    },
    spotlightBackgroundImage () {
      if (!this.homePage) {
        return
      }

      return this.$store.getters.getFileByName(this.homePage.background_image)
    },
    spotlightBackgroundVideo () {
      if (!this.homePage) {
        return
      }

      return this.$store.getters.getFileByName(this.homePage.background_video)
    }
  },
  created () {
    document.body.addEventListener('load', this.onImageLoad, true)
  },
  mounted () {
    // NB: if we load a page with the drawer open, scroll to the
    // corresponding section, so that when you close it, you're there
    if (this.superPageId) {
      const element = document.querySelector(`#${this.superPageId}`) || document.querySelector('.section-1')
      const rect = element.getBoundingClientRect()
      const y = rect.top + window.scrollY + this.offset
      // TODO sometimes this doesn't work on Chrome. Stupid
      this.$nextTick(() => {
        window.scrollTo(0, y)
        setTimeout(() => {
          window.scrollTo(0, y)
        }, 1000)
      })
    }
  },
  methods: {
    onClickNavigationContainer () {
      // escape route back to home
      this.$router.push('/')
    },
    onImageLoad (event) {
      var target = event.target
      if (target.tagName === 'IMG') {
        if (target.height > target.width && target.parentElement.tagName === 'FIGURE') {
          target.parentElement.classList.add('portrait')
        }
      }
    }
  }
}
</script>

<style lang="stylus">
@require '~vuetify/src/stylus/settings/_variables.styl'
@require '../assets/styles/base'

.home-name
  font-size: 140%;
  line-height: 0.7;
  border-bottom: 1px solid $font-color;
  display: inline-block;
  background-image: none;
  &:hover
    background-image: none;

.main-container
  position: relative;

.v-bottom-sheet
  background-color rgba(0, 0, 0, 0.8)
  border-top 1px solid $font-color
  padding 0.44rem
  transition-duration 0.5s !important
  display flex !important
  &:not(.v-dialog--active)
    transform translate3d(0, calc(100% - 0.94rem - 0.90rem), 0)
  .navigation a
    font-size 1.2rem

.v-dialog__content
  z-index 202 !important
  @media print
    display none

.v-overlay:before
  background-color: transparent !important

.v-overlay
  @media print
    display none

.home
  .v-navigation-drawer
    transition-duration: 0.5s !important
    overflow-y scroll
    z-index 7
    outline: none
  .navigation-column
    @media print
      display none
  .content-column
    @media print
      padding 0 !important
  .mt-100vh
    @media print
      margin-top 0 !important

.home-section
  // TODO: is this needed for the spotlight to work?
  // pointer-events: none
  @media print
    display none
</style>
